import {
  FilterStore,
  useAdvancedDueDateFilter,
  useAdvancedNumberFilter,
  useAdvancedSelectFilter,
  useAdvancedTextInputFilter,
  useAdvancedTimeFilter,
  useAdvancedUserFilter,
  useFilterValueStore,
  usePermitStatusFilter,
  usePermitTemplatesFilter,
  useSelectFilter,
} from '@core/providers/filters'
import { Filters } from 'webapp/components/elements/Filters/Filters'
import { MutableRefObject, useCallback, useEffect, useMemo } from 'react'
import { useQueryParamFilterStore } from 'webapp/components/elements/Filters'
import { useAdvancedDateFilter } from '@core/providers/filters/fundamentals/hooks/useAdvancedDateFilter'
import _debounce from 'lodash/debounce'
import { RegisterItemColumn } from '@core/entities/register/RegisterItemColumn'
import { RegisterFilterScopes } from '../types'
import { TableUserSummaryCard } from '@common/Table'
import {
  AllFilterTypes,
  registerFilterToStoreValue,
} from '@core/entities/filters'
import { RegisterDiagram } from '@core/entities/register/RegisterDiagram'
import { useAdvancedDiagramFilter } from '@core/providers/filters/fundamentals/hooks/useAdvancedDiagramFilter'
import { getRegisterItemsFilterQueryValues } from '@core/entities/register/Register/helpers'
import { localizedStrings } from '@core/strings'
import { TASK_STATUSES } from '@core/entities/task/TaskBase'
import { Task } from '@core/entities/task/Task'
import { AdvancedDueDateFilterOperators } from '@core/providers/filters/fundamentals/hooks/useAdvancedDueDateFilter'

type RegisterDetailsPropertyFiltersProps = {
  registerId?: number
  registerItemsColumns: RegisterItemColumn[]
  storeRef?: MutableRefObject<FilterStore<any>>
  useQueryParamStore?: boolean
  initialValues?: AllFilterTypes[]
  values?: AllFilterTypes[]
  scope: RegisterFilterScopes
  readOnly?: boolean
  noFlex?: boolean
  registerDiagrams?: RegisterDiagram[]
  setFilters: (filters: AllFilterTypes[]) => void
  pinnedFilterIds?: Array<string>
}

export const RegisterDetailsPropertyFilters: React.FC<
  RegisterDetailsPropertyFiltersProps
> = ({
  registerId,
  registerItemsColumns,
  storeRef,
  useQueryParamStore = true,
  initialValues,
  values,
  scope,
  readOnly,
  noFlex,
  registerDiagrams,
  setFilters,
  pinnedFilterIds = [],
}) => {
  const filters = {}
  const filterNames = {}

  const advancedDueDateFilterOptionOperators: {
    value: AdvancedDueDateFilterOperators
    label: string
  }[] = [
    { value: 'IS_DUE', label: localizedStrings.isDue },
    { value: 'IS_DUE_IN_LESS_THAN', label: localizedStrings.isDueInLessThan },
  ]

  registerItemsColumns.forEach((property) => {
    filterNames[property.id] = property.id
    const isPinned = !!pinnedFilterIds.includes(property.id)
    switch (property.type) {
      case 'STRING':
        filters[property.id] = useAdvancedTextInputFilter({
          label: property.name,
          isPinned,
        })
        break
      case 'SELECT':
        filters[property.id] = useAdvancedSelectFilter({
          label: property.name,
          options: property.selectConfig,
          isPinned,
        })
        break
      case 'DATE':
        filters[property.id] = useAdvancedDateFilter({
          // All dates are stored as the beginning of the day in UTC in the back-end
          UTC: true,
          label: property.name,
          dataTest: 'date-range-filter',
          isPinned,
        })
        break
      case 'NUMBER':
        filters[property.id] = useAdvancedNumberFilter({
          label: property.name,
          isPinned,
        })
        break
      case 'INCREMENTAL_ID':
        filters[property.id] = useAdvancedTextInputFilter({
          label: property.name,
          isPinned,
        })
        break
      case 'USER':
        filters[property.id] = useAdvancedUserFilter({
          label: property.name,
          organisationId: scope.organisationId,
          projectId: scope.type === 'PROJECT' && scope.projectId,
          renderItem: (user) => <TableUserSummaryCard user={user} />,
          isPinned,
        })
        break
      case 'DIAGRAM':
        filters[property.id] = useAdvancedDiagramFilter({
          label: property.name,
          registerId,
          isPinned,
        })
        break
      case 'INSPECTION':
        filters[property.id] = useAdvancedDueDateFilter({
          label: property.name,
          isPinned,
        })
        break
      case 'TIME':
        filters[property.id] = useAdvancedTimeFilter({
          // All dates are stored as the beginning of the day in UTC in the back-end
          UTC: true,
          label: property.name,
          dataTest: 'time-range-filter',
          isPinned,
        })
        break
      case 'PERMIT':
        filterNames[property.id + '-status'] = property.id + '-status'
        filters[property.id + '-status'] = usePermitStatusFilter({
          label: `${localizedStrings.permit} - ${localizedStrings.status}`,
          multiple: true,
          whitelist: [
            'REQUESTED',
            'SENT_TO_PERMIT_HOLDER',
            'ISSUED',
            'OPEN',
            'EXTENSION_REQUESTED',
            'SUSPENDED',
            'EXPIRED',
            'AWAITING_CHECKS',
            'AWAITING_FINAL_SIGN_OFF',
          ],
        })
        filterNames[property.id + '-template'] = property.id + '-template'
        filters[property.id + '-template'] = usePermitTemplatesFilter({
          label: `${localizedStrings.permit} - ${localizedStrings.template}`,
          scope: scope,
          multiple: true,
        })
        break
      case 'TASK':
        filterNames[property.id + '-status'] = property.id + '-status'
        filters[property.id + '-status'] = useSelectFilter({
          label: `${localizedStrings.task} - ${localizedStrings.status}`,
          options: Object.values(TASK_STATUSES)
            .filter((status) => status !== 'DRAFT')
            .map((status) => status),
          nothingSelectedText: localizedStrings.allStatuses,
          valueToLabel: (value) => Task.statusString(value),
          multiple: true,
        })
        filterNames[property.id + '-dueDate'] = property.id + '-dueDate'
        filters[property.id + '-dueDate'] = useAdvancedDueDateFilter({
          label: `${localizedStrings.task} - ${localizedStrings.dueDate}`,
          advancedFilterOptionOperators: advancedDueDateFilterOptionOperators,
        })
        break
      default:
        break
    }
  })

  const initialFilterValues = useMemo(
    () =>
      initialValues?.reduce((result, filter) => {
        result[filter.propertyId] = {
          ...filter,
          value: registerFilterToStoreValue(filter),
        }
        return result
      }, {}),
    [],
  )

  const filterValues = useMemo(
    () =>
      values?.reduce((result, filter) => {
        result[filter.propertyId] = {
          ...filter,
          value: registerFilterToStoreValue(filter),
        }
        return result
      }, {}),
    [values],
  )

  const filterStore = useQueryParamStore
    ? useQueryParamFilterStore({
        filters: filters,
        queryParams: filterNames,
        onChangeOverride: {
          page: 1,
        },
      })
    : // We don't use a query value store when editing Register Template Filters
      useFilterValueStore({
        filters,
        initialValues: { ...initialFilterValues },
        ...(values ? { values: { ...filterValues } } : {}),
      })

  useEffect(() => {
    if (storeRef) {
      storeRef.current = filterStore
    }
  }, [])

  const debouncedFilter = useCallback(
    _debounce((filters) => setFilters(filters), 300),
    [],
  )

  useEffect(() => {
    const filters = getRegisterItemsFilterQueryValues(
      registerItemsColumns,
      filterStore.values,
    )

    debouncedFilter(filters)
  }, [filterStore.values])

  return (
    <Filters
      noFlex={noFlex}
      filters={filters}
      store={filterStore}
      readOnly={readOnly}
    />
  )
}
